import { useCallback, useEffect, useRef, useState } from "react";
import jarvisAvatar from "../assets/trustjarvis.jpg";

// ── Data ─────────────────────────────────────────────────────────────────────

const INDEX_CARDS = [
  {
    name: "Precious Metals",
    image: "https://ipfs.avo.so/ipfs/bafybeifgp5hy5zlzabhtnpzgvmvjv7y2uodle2umoumc4oyenu2kveto7i",
    accentColor: "#22D3EE",
    badgeBg: "rgba(34,211,238,0.1)",
    description: "Tokenized Gold, Silver, Platinum & more.",
    positions: ["GLDr 20%", "SLVr 20%", "PPLTr 20%", "+2 more"],
    chartPath: "M0,50 Q50,45 100,30 T200,10",
    chartGradient: "from-[#22D3EE]/20",
    tvl: "$1.14M",
    returnLabel: "30d Return",
    returnValue: "+13.58%",
  },
  {
    name: "Meteora Index",
    image: "https://ipfs.avo.so/ipfs/bafybeibmg7oao2mtocdnoc3iroyhbipfur6pnb35gf5kjoreqrhyoxlmty",
    accentColor: "#9EFF00",
    badgeBg: "rgba(158,255,0,0.1)",
    description: "Auto-rebalancing of Meteora's top ICM tokens.",
    positions: ["MET 40%", "DUPE 24%", "UMBRA 18%", "+7 more"],
    chartPath: "M0,30 Q30,25 60,35 T120,20 T200,40",
    chartGradient: "from-[#9EFF00]/20",
    tvl: "$738.9K",
    returnLabel: "7d Return",
    returnValue: "+5.33%",
  },
  {
    name: "Mag 7 Index",
    image: "https://ipfs.avo.so/ipfs/bafybeietgtv5d2wlowewd6egiz5mpsxkdtm5hg3bh43uqdktpfwhmby5da",
    accentColor: "#A855F7",
    badgeBg: "rgba(168,85,247,0.1)",
    description: "Tokenized Magnificent 7 stocks, on-chain.",
    positions: ["NVDAx 22%", "AAPLx 19%", "GOOGLx 18%", "+4 more"],
    chartPath: "M0,40 Q50,38 100,35 T200,30",
    chartGradient: "from-[#A855F7]/20",
    tvl: "$996.5K",
    returnLabel: "7d Return",
    returnValue: "+1.92%",
  },
];

// ── Types ────────────────────────────────────────────────────────────────────

type Msg =
  | { id: number; kind: "user"; text: string }
  | { id: number; kind: "user-green"; text: string }
  | { id: number; kind: "jarvis"; text: string; dim?: boolean }
  | { id: number; kind: "jarvis-rich" }
  | { id: number; kind: "cards" }
  | { id: number; kind: "tool-pill"; fadingOut: boolean };

type MsgInput =
  | { kind: "user"; text: string }
  | { kind: "user-green"; text: string }
  | { kind: "jarvis"; text: string; dim?: boolean }
  | { kind: "jarvis-rich" }
  | { kind: "cards" }
  | { kind: "tool-pill"; fadingOut: boolean };

// ── Sub-components ───────────────────────────────────────────────────────────

function JarvisAvatarImg({ px = 32 }: { px?: number }) {
  return (
    <img
      src={jarvisAvatar}
      alt="Miles"
      className="rounded-full flex-shrink-0 object-cover"
      style={{ width: px, height: px }}
    />
  );
}

function IndexCard({ card }: { card: (typeof INDEX_CARDS)[0] }) {
  return (
    <div
      style={{
        background: "rgba(15,15,17,0.6)",
        backdropFilter: "blur(12px)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: "14px",
        padding: "10px",
        minWidth: "175px",
        flexShrink: 0,
      }}
    >
      {/* Header: image + INDEX badge */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "6px",
        }}
      >
        <img
          src={card.image}
          alt={card.name}
          style={{
            width: 32,
            height: 32,
            borderRadius: "8px",
            border: "1px solid rgba(255,255,255,0.1)",
            objectFit: "cover",
          }}
        />
        <div
          style={{
            padding: "2px 6px",
            borderRadius: "4px",
            background: card.badgeBg,
            color: card.accentColor,
            fontSize: "8px",
            fontWeight: 700,
            letterSpacing: "0.03em",
          }}
        >
          INDEX
        </div>
      </div>

      {/* Title + description */}
      <div
        style={{
          fontSize: "12px",
          fontWeight: 700,
          color: "#fff",
          lineHeight: 1.2,
          marginBottom: "2px",
        }}
      >
        {card.name}
      </div>
      <div
        style={{
          fontSize: "9px",
          color: "#666",
          lineHeight: 1.3,
          marginBottom: "6px",
        }}
      >
        {card.description}
      </div>

      {/* Positions as mono tags */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "4px 8px",
          fontSize: "9px",
          fontFamily: "monospace",
          color: "#555",
          marginBottom: "6px",
        }}
      >
        {card.positions.map((p) => (
          <span key={p}>{p}</span>
        ))}
      </div>

      {/* Mini chart */}
      <div
        style={{
          width: "100%",
          height: 28,
          borderRadius: "6px",
          overflow: "hidden",
          position: "relative",
          marginBottom: "6px",
          background: `linear-gradient(to top, ${card.accentColor}20, transparent)`,
        }}
      >
        <svg
          style={{
            position: "absolute",
            bottom: 0,
            width: "100%",
            height: "100%",
          }}
          viewBox="0 0 200 60"
          preserveAspectRatio="none"
        >
          <path
            d={card.chartPath}
            fill="none"
            stroke={card.accentColor}
            strokeWidth="2"
          />
        </svg>
      </div>

      {/* TVL + Return */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: "10px",
        }}
      >
        <div>
          <div style={{ color: "#555", fontSize: "8px", marginBottom: "1px" }}>
            TVL
          </div>
          <div style={{ color: "#fff", fontFamily: "monospace", fontWeight: 600 }}>
            {card.tvl}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ color: "#555", fontSize: "8px", marginBottom: "1px" }}>
            {card.returnLabel}
          </div>
          <div
            style={{
              color: "#9EFF00",
              fontFamily: "monospace",
              fontWeight: 600,
            }}
          >
            {card.returnValue}
          </div>
        </div>
      </div>
    </div>
  );
}

function renderMsg(msg: Msg) {
  if (msg.kind === "user") {
    return (
      <div className="flex justify-end">
        <div className="bg-[#2D2D33] text-white px-5 py-3 rounded-2xl rounded-tr-none max-w-[82%]">
          <p className="text-sm">{msg.text}</p>
        </div>
      </div>
    );
  }

  if (msg.kind === "user-green") {
    return (
      <div className="flex justify-end">
        <div
          className="bg-avo-lime text-black px-5 py-3 rounded-2xl rounded-tr-none max-w-[82%] font-semibold"
          style={{ boxShadow: "0 0 15px rgba(158,255,0,0.25)" }}
        >
          <p className="text-sm">{msg.text}</p>
        </div>
      </div>
    );
  }

  if (msg.kind === "jarvis") {
    return (
      <div className="flex justify-start gap-3">
        <JarvisAvatarImg px={32} />
        <div className="bg-[#151518] border border-white/5 text-gray-200 px-5 py-3 rounded-2xl rounded-tl-none max-w-[85%]">
          <p className={`text-sm${msg.dim ? " text-gray-400" : ""}`}>
            {msg.text}
          </p>
        </div>
      </div>
    );
  }

  if (msg.kind === "jarvis-rich") {
    return (
      <div className="flex justify-start gap-3">
        <JarvisAvatarImg px={32} />
        <div className="bg-[#151518] border border-white/5 text-gray-200 px-5 py-3 rounded-2xl rounded-tl-none max-w-[85%]">
          <p className="text-sm">
            Done ✅ — $200 allocated to{" "}
            <span className="font-bold" style={{ color: "#c8a84b" }}>
              Precious Metals
            </span>
            {". I'll rebalance automatically."}
          </p>
        </div>
      </div>
    );
  }

  if (msg.kind === "cards") {
    return (
      <div
        style={{
          display: "flex",
          gap: "8px",
          overflowX: "auto",
          padding: "4px 2px 8px",
          scrollbarWidth: "none",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {INDEX_CARDS.map((card) => (
          <IndexCard key={card.name} card={card} />
        ))}
      </div>
    );
  }

  if (msg.kind === "tool-pill") {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "flex-start",
          opacity: msg.fadingOut ? 0 : 1,
          transform: msg.fadingOut ? "translateY(-4px)" : "translateY(0)",
          transition: "opacity 0.4s ease, transform 0.3s ease",
          marginBottom: "8px",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: "999px",
            padding: "5px 12px",
          }}
        >
          <svg
            style={{
              width: 10,
              height: 10,
              flexShrink: 0,
              animation: "spin 1s linear infinite",
            }}
            viewBox="0 0 24 24"
            fill="none"
            stroke="#9EFF00"
            strokeWidth="2.5"
          >
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
          <span
            style={{
              fontSize: "11px",
              color: "#666",
              fontFamily: "monospace",
              letterSpacing: "0.02em",
            }}
          >
            search_portfolios()
          </span>
        </div>
      </div>
    );
  }

  return null;
}

// ── Main component ───────────────────────────────────────────────────────────

export default function JarvisSlide({ isActive }: { isActive: boolean }) {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [showTyping, setShowTyping] = useState(false);

  const chatWindowRef = useRef<HTMLDivElement>(null);
  const genRef = useRef(0);
  const msgIdRef = useRef(0);

  const scrollToBottom = useCallback(() => {
    const el = chatWindowRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, showTyping, scrollToBottom]);

  const runAnimation = useCallback(() => {
    const gen = ++genRef.current;
    msgIdRef.current = 0;
    setMessages([]);
    setShowTyping(false);

    const sleep = (ms: number) =>
      new Promise<void>((resolve, reject) => {
        setTimeout(() => (genRef.current !== gen ? reject() : resolve()), ms);
      });

    const addMsg = (msg: MsgInput): number => {
      const id = msgIdRef.current++;
      setMessages((prev) => [...prev, { ...msg, id } as Msg]);
      return id;
    };

    const go = async () => {
      try {
        await sleep(600);

        addMsg({
          kind: "user",
          text: "What are the top performing indexes?",
        });

        await sleep(900);
        setShowTyping(true);
        scrollToBottom();

        await sleep(1400);
        setShowTyping(false);
        const toolId = addMsg({ kind: "tool-pill", fadingOut: false });
        scrollToBottom();

        await sleep(1600);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === toolId ? { ...m, fadingOut: true } : m
          )
        );

        await sleep(400);
        setMessages((prev) => prev.filter((m) => m.id !== toolId));

        setShowTyping(true);
        scrollToBottom();

        await sleep(1000);
        setShowTyping(false);

        addMsg({
          kind: "jarvis",
          text: "Here are the top 3 performing indexes right now:",
        });
        scrollToBottom();

        await sleep(300);
        addMsg({ kind: "cards" });
        scrollToBottom();

        await sleep(400);
        addMsg({
          kind: "jarvis",
          text: "Want me to allocate funds to any of these?",
          dim: true,
        });
        scrollToBottom();

        await sleep(1600);
        addMsg({
          kind: "user-green",
          text: "Allocate $200 to the precious metals one.",
        });
        scrollToBottom();

        await sleep(800);
        setShowTyping(true);
        scrollToBottom();

        await sleep(1400);
        setShowTyping(false);
        addMsg({ kind: "jarvis-rich" });
        scrollToBottom();

        await sleep(4000);
        setMessages([]);
        setShowTyping(false);
        go();
      } catch {
        // generation changed — this loop is dead
      }
    };

    go();
  }, [scrollToBottom]);

  useEffect(() => {
    if (isActive) {
      runAnimation();
    } else {
      genRef.current++;
      setMessages([]);
      setShowTyping(false);
    }
    return () => {
      genRef.current++;
    };
  }, [isActive, runAnimation]);

  return (
    <div className="max-w-7xl w-full mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
      {/* LEFT: text */}
      <div className="space-y-8">
        <div className="inline-block px-4 py-1 rounded-full bg-gradient-to-r from-avo-cyan/20 to-avo-purple/20 border border-avo-cyan/30 text-avo-cyan text-sm font-pixel mb-2">
          MEET MILES
        </div>
        <h2 className="text-5xl md:text-6xl font-bold leading-tight">
          Talk to Miles.
          <br />
          <span className="text-avo-lime">He listens.</span>
        </h2>
        <p className="text-lg text-gray-400 mb-2">You can do things like:</p>

        <div className="space-y-4">
          {[
            `"Sell half my SOL if it doubles from here"`,
            `"Move my idle USDC to wherever the APY is highest"`,
            `"Rebalance my portfolio weekly to match the S&P crypto index"`,
          ].map((text) => (
            <div key={text} className="flex items-center gap-3 text-gray-300">
              <div className="w-6 h-6 rounded-full bg-avo-lime/20 flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-avo-lime"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <span className="text-lg">{text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* RIGHT: animated chat window */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-avo-purple/20 to-transparent blur-2xl -z-10 rounded-full transform scale-90" />
        <div className="bg-[#151518] border border-white/10 rounded-3xl overflow-hidden shadow-2xl">
          {/* Gradient top bar */}
          <div className="h-1.5 w-full bg-gradient-to-r from-avo-lime to-avo-purple" />

          {/* Header */}
          <div className="p-4 border-b border-white/5 flex items-center gap-3 bg-[#0E0E11]">
            <div className="relative">
              <img
                src={jarvisAvatar}
                alt="Miles"
                className="w-10 h-10 rounded-full object-cover"
              />
              <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-[#0E0E11] rounded-full" />
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-white leading-none">Miles AI</h4>
              <span className="text-xs text-avo-lime font-mono">
                Online • v2.4
              </span>
            </div>
            <button
              onClick={() => runAnimation()}
              className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 hover:border-white/20 transition-all cursor-pointer"
              title="Restart chat"
            >
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>

          {/* Chat messages */}
          <div
            ref={chatWindowRef}
            className="p-6 bg-[#030304]/50 overflow-hidden relative"
            style={{ height: "420px" }}
          >
            <div className="space-y-4">
              {messages.map((msg) => (
                <div key={msg.id} className="animate-fade-in">
                  {renderMsg(msg)}
                </div>
              ))}
            </div>

            {/* Typing indicator */}
            {showTyping && (
              <div className="flex justify-start gap-3 mt-4 animate-fade-in">
                <JarvisAvatarImg px={32} />
                <div className="bg-[#151518] border border-white/5 px-5 py-3 rounded-2xl rounded-tl-none flex items-center gap-1.5">
                  <span
                    className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                    style={{ animationDelay: "0ms" }}
                  />
                  <span
                    className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                    style={{ animationDelay: "150ms" }}
                  />
                  <span
                    className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                    style={{ animationDelay: "300ms" }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Input bar */}
          <div className="p-4 border-t border-white/5 bg-[#0E0E11]">
            <div className="bg-[#151518] h-12 rounded-xl flex items-center px-4 border border-white/5 opacity-60">
              <span className="text-gray-500 text-sm">
                Type a message...
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
